import React from "react";
import {
  Box,
  Paper,
  Checkbox,
  TextField,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Select,
  MenuItem,
  Button,
  Tooltip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { ExpandMore, AutoAwesome, ArrowForward } from "@mui/icons-material";
import { ColumnMapping } from "./types";
import { BulletPointType } from "../../../../pages/playground/types";

// All bullet point types available for mapping (Custom at the end)
const BULLET_POINT_TYPES: {
  value: BulletPointType;
  label: string;
}[] = [
  { value: BulletPointType.GARMENT_TYPE, label: "Garment Type" },
  {
    value: BulletPointType.SPECIFIC_GARMENT_TYPE,
    label: "Specific Garment Type",
  },
  { value: BulletPointType.GENDER, label: "Gender" },
  { value: BulletPointType.COMPOSITION, label: "Composition" },
  { value: BulletPointType.FABRIC, label: "Fabric" },
  { value: BulletPointType.WEAVE, label: "Weave" },
  { value: BulletPointType.COLOR, label: "Color" },
  { value: BulletPointType.SIZES, label: "Sizes" },
  { value: BulletPointType.FIT, label: "Fit" },
  { value: BulletPointType.WEIGHT, label: "Weight" },
  { value: BulletPointType.MONOGRAM, label: "Monogram" },
  { value: BulletPointType.PROPER_NOUN, label: "Proper Noun" },
  { value: BulletPointType.SEASON, label: "Season" },
  { value: BulletPointType.COUNTRY_OF_ORIGIN, label: "Country of Origin" },
  { value: BulletPointType.CONTEXT_OF_USE, label: "Context of Use" },
  { value: BulletPointType.DETAIL, label: "Detail" },
];

export interface CustomBulletPointsSectionProps {
  columnMappings: ColumnMapping[];
  onToggleColumn: (index: number) => void;
  onCustomNameChange: (index: number, value: string) => void;
  onDescriptionChange: (index: number, value: string) => void;
  onMappedTypeChange: (
    index: number,
    type: BulletPointType | "CUSTOM" | undefined
  ) => void;
  onGenerateDescription: (index: number) => void;
  selectedCount: number;
  validationErrorCount: number;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  loading: boolean;
}

export const CustomBulletPointsSection: React.FC<
  CustomBulletPointsSectionProps
> = ({
  columnMappings,
  onToggleColumn,
  onCustomNameChange,
  onDescriptionChange,
  onMappedTypeChange,
  onGenerateDescription,
  selectedCount,
  validationErrorCount,
  expanded,
  onExpandedChange,
  loading,
}) => {
  // Get already-selected bullet point types (except DETAIL which can be selected multiple times)
  const usedBulletPointTypes = new Set(
    columnMappings
      .filter(
        (m) =>
          m.mapped_to_type &&
          m.mapped_to_type !== "CUSTOM" &&
          m.mapped_to_type !== BulletPointType.DETAIL
      )
      .map((m) => m.mapped_to_type)
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandedChange(isExpanded)}
      sx={{ mb: 2 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ backgroundColor: "action.hover" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Bullet Point Mappings
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Map columns from your master file to bullet points
            </Typography>
          </Box>
          {selectedCount > 0 && (
            <Chip
              label={`${selectedCount} selected`}
              size="small"
              color="secondary"
            />
          )}
          {validationErrorCount > 0 && (
            <Chip
              label={`${validationErrorCount} error${
                validationErrorCount > 1 ? "s" : ""
              }`}
              size="small"
              color="error"
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select columns for custom mappings. You can map to existing bullet
              point types or create custom ones. Adding a description is later
              used for AI generation.
            </Typography>

            {columnMappings.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No columns available for mapping
                </Typography>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  maxHeight: 600,
                  overflowY: "auto",
                }}
              >
                {columnMappings.map((mapping, index) => {
                  const isCustom = mapping.mapped_to_type === "CUSTOM";

                  // Only show validation styling when the column is selected
                  const showValidation = mapping.selected;

                  // Check if the selection is valid (has a type or custom name)
                  const hasValidBulletPoint =
                    (mapping.mapped_to_type &&
                      mapping.mapped_to_type !== "CUSTOM") ||
                    (isCustom && mapping.custom_name.trim().length > 0);

                  // Check if a type is disabled (already used by another column, except DETAIL)
                  const isTypeDisabled = (type: BulletPointType) => {
                    if (type === BulletPointType.DETAIL) return false; // DETAIL can be selected multiple times
                    if (mapping.mapped_to_type === type) return false; // Current selection is not disabled
                    return usedBulletPointTypes.has(type);
                  };

                  // Get the bullet point name for AI description generation
                  const bulletPointNameForAI =
                    mapping.mapped_to_type &&
                    mapping.mapped_to_type !== "CUSTOM"
                      ? mapping.mapped_to_type
                      : mapping.custom_name.trim();
                  const canGenerateDescription =
                    bulletPointNameForAI.length > 0;

                  // Description is required when a bullet point type is selected
                  const hasDescription = mapping.description.trim().length > 0;

                  // Check if this column has validation errors (selected but incomplete)
                  const hasValidationError =
                    mapping.selected &&
                    (!hasValidBulletPoint || !hasDescription);

                  return (
                    <Paper
                      key={mapping.column_name}
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        backgroundColor: "background.paper",
                        border: mapping.selected ? "2px solid" : "1px solid",
                        borderColor: mapping.selected
                          ? hasValidationError
                            ? "error.main"
                            : "primary.main"
                          : "divider",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {/* Header: Checkbox + Column Name (left) + Dropdown/TextField (right) */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                          pb: 1.5,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                          }}
                          onClick={() => onToggleColumn(index)}
                        >
                          <Checkbox
                            checked={mapping.selected}
                            onChange={() => onToggleColumn(index)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {mapping.column_name}
                          </Typography>
                        </Box>

                        {/* Arrow to indicate mapping direction */}
                        <ArrowForward
                          sx={{
                            color: "text.disabled",
                            fontSize: "1.25rem",
                            mx: 1,
                          }}
                        />

                        {/* Show TextField when Custom is selected, otherwise show dropdown */}
                        {isCustom ? (
                          <TextField
                            size="small"
                            value={mapping.custom_name}
                            onChange={(e) => {
                              if (!mapping.selected) onToggleColumn(index);
                              onCustomNameChange(index, e.target.value);
                            }}
                            placeholder="Enter custom bullet point name"
                            sx={{
                              minWidth: 250,
                              "& .MuiOutlinedInput-root": {
                                ...(showValidation && {
                                  "& fieldset": {
                                    borderColor: hasValidBulletPoint
                                      ? "success.main"
                                      : "error.main",
                                    borderWidth: 2,
                                  },
                                  "&:hover fieldset": {
                                    borderColor: hasValidBulletPoint
                                      ? "success.dark"
                                      : "error.dark",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: hasValidBulletPoint
                                      ? "success.main"
                                      : "error.main",
                                  },
                                }),
                              },
                            }}
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Tooltip title="Switch to existing bullet point">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          onMappedTypeChange(index, undefined);
                                        }}
                                      >
                                        <ExpandMore sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        ) : (
                          <Select
                            size="small"
                            value={mapping.mapped_to_type || ""}
                            displayEmpty
                            onChange={(e) => {
                              if (!mapping.selected) onToggleColumn(index);
                              const value = e.target.value;
                              if (value === "CUSTOM") {
                                onMappedTypeChange(index, "CUSTOM");
                              } else if (value) {
                                onMappedTypeChange(
                                  index,
                                  value as BulletPointType
                                );
                              }
                            }}
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <em style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                    Select bullet point
                                  </em>
                                );
                              }
                              const found = BULLET_POINT_TYPES.find(
                                (t) => t.value === selected
                              );
                              return found ? found.label : selected;
                            }}
                            sx={{
                              minWidth: 250,
                              ...(showValidation && {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: hasValidBulletPoint
                                    ? "success.main"
                                    : "error.main",
                                  borderWidth: 2,
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: hasValidBulletPoint
                                    ? "success.dark"
                                    : "error.dark",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: hasValidBulletPoint
                                      ? "success.main"
                                      : "error.main",
                                  },
                              }),
                            }}
                          >
                            {BULLET_POINT_TYPES.map((type) => {
                              const disabled = isTypeDisabled(type.value);
                              return (
                                <MenuItem
                                  key={type.value}
                                  value={type.value}
                                  disabled={disabled}
                                >
                                  {type.label}
                                  {disabled && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.disabled"
                                      sx={{ ml: 1 }}
                                    >
                                      (already used)
                                    </Typography>
                                  )}
                                </MenuItem>
                              );
                            })}
                            <MenuItem value="CUSTOM">
                              <em>Custom...</em>
                            </MenuItem>
                          </Select>
                        )}
                      </Box>

                      {/* Content: Description, Sample Values */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* Description field with AI generate button above */}
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Description
                            </Typography>
                            <Tooltip
                              title={
                                canGenerateDescription
                                  ? "Generate description with AI"
                                  : "Select a bullet point type first"
                              }
                            >
                              <span>
                                <Button
                                  onClick={() => {
                                    if (!mapping.selected)
                                      onToggleColumn(index);
                                    onGenerateDescription(index);
                                  }}
                                  disabled={
                                    mapping.isGeneratingDescription ||
                                    !canGenerateDescription
                                  }
                                  variant="outlined"
                                  size="small"
                                  startIcon={
                                    mapping.isGeneratingDescription ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <AutoAwesome sx={{ fontSize: 16 }} />
                                    )
                                  }
                                  sx={{ textTransform: "none" }}
                                >
                                  AI Generate
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                          <TextField
                            value={mapping.description}
                            onChange={(e) =>
                              onDescriptionChange(index, e.target.value)
                            }
                            onFocus={() => {
                              if (!mapping.selected) onToggleColumn(index);
                            }}
                            placeholder={
                              hasValidBulletPoint
                                ? "Describe what this field represents"
                                : "Select a bullet point type first"
                            }
                            disabled={!hasValidBulletPoint}
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            variant="outlined"
                            slotProps={{ htmlInput: { maxLength: 500 } }}
                            helperText={`${mapping.description.length}/500`}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                // Validation styling when a bullet point type is selected
                                ...(showValidation &&
                                  hasValidBulletPoint && {
                                    "& fieldset": {
                                      borderColor: hasDescription
                                        ? "success.main"
                                        : "error.main",
                                      borderWidth: 2,
                                    },
                                    "&:hover fieldset": {
                                      borderColor: hasDescription
                                        ? "success.dark"
                                        : "error.dark",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: hasDescription
                                        ? "success.main"
                                        : "error.main",
                                    },
                                  }),
                              },
                            }}
                          />
                        </Box>

                        {/* Sample Values */}
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            sx={{ mb: 1, display: "block" }}
                          >
                            Sample Values
                          </Typography>
                          {mapping.sample_values.length > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.75,
                              }}
                            >
                              {mapping.sample_values.map((val, i) => (
                                <Tooltip key={i} title={val} placement="top">
                                  <Chip
                                    label={val}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.75rem",
                                      maxWidth: 300,
                                      "& .MuiChip-label": {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      },
                                    }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              fontStyle="italic"
                            >
                              No sample values available
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
